import React, { useMemo, useEffect, useState } from "react";
import { Box, Grid, Typography, Button, Modal } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { MODES_OPTIONS } from "@app/types/generics.types";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { useTypesUncertainty, useUser } from "@services/servicesGenericsTables";
import { setsUncertaintyForm } from "../utils/contants";

interface UncertaintyFormProps {
  mode: MODES_OPTIONS;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

const UncertaintyForm = ({ mode }: UncertaintyFormProps) => {
  const form = useFormContext();
  const { userList } = useUser();
  const { typesUncertaintyList } = useTypesUncertainty();
  const [disableUncertainty, setDisableUncertainty] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const isView = mode === MODES_OPTIONS.VIEW;
  const isEdit = mode === MODES_OPTIONS.EDIT;
  const isNew = mode === MODES_OPTIONS.NEW;

  const updatedSetsUncertaintyForm = useMemo(() => {
    if (!typesUncertaintyList || typesUncertaintyList.length === 0) {
      return [];
    }

    return setsUncertaintyForm.map((item) => {
      const match = typesUncertaintyList.find(
        (element) => element.nombre === item.label
      );
      return {
        ...item,
        id: match ? match.id : null,
      };
    });
  }, [typesUncertaintyList]);

  useEffect(() => {
    if (isEdit && form.getValues("tipo_incertidumbre")) {
      const initialUncertaintyType = updatedSetsUncertaintyForm.find(
        (item) => item.id === form.getValues("tipo_incertidumbre")?.id
      );
      if (initialUncertaintyType) {
        form.setValue("tipo_incertidumbre", initialUncertaintyType);
      }
    }
  }, [isEdit, updatedSetsUncertaintyForm, JSON.stringify(form.getValues())]);

  const selectedTypeUncertainty = form.watch("tipo_incertidumbre");

  const activeSet = useMemo(() => {
    if (!selectedTypeUncertainty) return null;
    return updatedSetsUncertaintyForm.find(
      (item) => item.id === selectedTypeUncertainty?.id
    );
  }, [selectedTypeUncertainty, updatedSetsUncertaintyForm]);

  const CurrentComponent = useMemo(() => {
    if (activeSet && activeSet.component) {
      return activeSet.component;
    }
    return null;
  }, [activeSet]);

  useEffect(() => {
    const dirtyFields = form.formState.dirtyFields;
    const filteredDirtyFields = Object.keys(dirtyFields).filter(
      (key) => key !== "tipo_incertidumbre"
    );

    const hasDirtyFields = filteredDirtyFields.some((key) => dirtyFields[key]);
    const isDisabled = isNew && hasDirtyFields;

    setDisableUncertainty(isDisabled);
  }, [form.watch()]);

  const handleChangeUncertainty = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    form.reset();
    setDisableUncertainty(false);
    setOpen(false);
  };

  return (
    <>
      <Box mt={2}>
        <ResponsiveFormContainer columns={5}>
          <MAutocomplete
            key={`tipo_incertidumbre-${selectedTypeUncertainty}`}
            name="tipo_incertidumbre"
            options={updatedSetsUncertaintyForm}
            label="Tipo de incertidumbre"
            getOptionLabel={(option) => option.label || ""}
            validation={{ required: "El tipo de incertidumbre es requerido" }}
            disabled={isView || disableUncertainty || isEdit}
          />

          <MAutocomplete
            key={form.watch("analyst")}
            name="analyst"
            options={userList}
            label="Analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          {disableUncertainty && (
            <Box mt={2}>
              <Button variant="outlined" onClick={handleChangeUncertainty}>
                Cambiar Tipo de Incertidumbre
              </Button>
            </Box>
          )}
        </ResponsiveFormContainer>

        <Grid item xs={12} my={3}>
          <Typography variant="tagText">{"Nueva incertidumbre"}</Typography>
        </Grid>

        <ResponsiveFormContainer columns={5}>
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha Incertidumbre"
            validation={{
              required: "La fecha es requerida",
            }}
            disabled={isView}
          />
        </ResponsiveFormContainer>
      </Box>

      <Box mt={2}>
        {CurrentComponent ? (
          <CurrentComponent mode={mode} />
        ) : (
          <Typography variant="body1">
            Selecciona un tipo de incertidumbre para continuar.
          </Typography>
        )}
      </Box>

      <Box mt={4}>
        <ResponsiveFormContainer columns={3}>
          <MTextField
            name="internal_comments"
            label="Comentarios Internos"
            placeholder="Comentarios Internos"
            type="textArea"
            rows={4}
            multiline
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2, borderBottom: 3, borderColor: "divider" }}
          >
            Va a cambiar el tipo de incertidumbre y la información ingresada se
            perderá. ¿Desea continuar?
          </Typography>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ mt: 2 }}
            display="flex"
            gap={2}
            justifyContent="center"
          >
            <Button variant="contained" onClick={handleConfirm}>
              Aceptar
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              Cerrar
            </Button>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default UncertaintyForm;
